import fetchApi from "./fetch";

export type FbsOrdersRangeParams = {
  from?: string;
  to?: string;
  marketplace?: string;
  tag_ids?: string;
  search?: string;
};

export type FbsOrdersSummary = {
  from: string;
  to: string;
  units_ordered: number;
  units_active: number;
  units_cancelled: number;
  cancel_rate: number;
  revenue_ordered: number;
  revenue_active: number;
  revenue_cancelled: number;
  lines_count: number;
  orders_count: number;
};

export type FbsOrdersArticleRow = {
  article: string;
  name: string | null;
  marketplace: string;
  units_ordered: number;
  units_active: number;
  units_cancelled: number;
  cancel_rate: number;
  revenue_ordered: number;
  revenue_active: number;
  revenue_cancelled: number;
};

export type FbsOrdersByArticle = {
  from: string;
  to: string;
  rows: FbsOrdersArticleRow[];
};

export type FbsOrdersByHour = {
  from: string;
  to: string;
  timezone: string;
  hours: Array<{
    hour: number;
    units_ordered: number;
    units_active: number;
    units_cancelled: number;
  }>;
};

export type FbsOrdersByDay = {
  from: string;
  to: string;
  timezone: string;
  days: Array<{
    day: string;
    units_ordered: number;
    units_active: number;
    units_cancelled: number;
    revenue_ordered: number;
    revenue_active: number;
    revenue_cancelled: number;
  }>;
};

export type FbsOrdersHeatmap = {
  from: string;
  to: string;
  timezone: string;
  cells: Array<{ dow: number; hour: number; units_ordered: number }>;
};

export type FbsOrdersCancellations = {
  from: string;
  to: string;
  units_ordered: number;
  units_cancelled: number;
  cancel_rate: number;
  by_source: Array<{ cancel_source: string; units_cancelled: number }>;
  by_day: Array<{
    day: string;
    units_ordered: number;
    units_cancelled: number;
    cancel_rate: number;
  }>;
  top_articles: Array<{
    article: string;
    marketplace: string;
    units_ordered: number;
    units_cancelled: number;
    cancel_rate: number;
  }>;
};

const qs = (params: Record<string, string | undefined>) => {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== "") sp.set(k, v);
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
};

export const getFbsOrdersSummary = async (
  params: FbsOrdersRangeParams
): Promise<FbsOrdersSummary> => {
  const res = await fetchApi(`/api/fbs_orders/summary${qs(params)}`);
  return res.json();
};

export const getFbsOrdersByArticle = async (
  params: FbsOrdersRangeParams & {
    limit?: string;
    sort?: string;
    order?: string;
  }
): Promise<FbsOrdersByArticle> => {
  const res = await fetchApi(`/api/fbs_orders/by_article${qs(params)}`);
  return res.json();
};

export const downloadFbsOrdersByArticleCsv = async (
  params: FbsOrdersRangeParams & {
    sort?: string;
    order?: string;
  }
) => {
  return fetchApi(`/api/fbs_orders/by_article/csv${qs(params)}`);
};

export const getFbsOrdersByHour = async (
  params: FbsOrdersRangeParams
): Promise<FbsOrdersByHour> => {
  const res = await fetchApi(`/api/fbs_orders/by_hour${qs(params)}`);
  return res.json();
};

export const getFbsOrdersByDay = async (
  params: FbsOrdersRangeParams
): Promise<FbsOrdersByDay> => {
  const res = await fetchApi(`/api/fbs_orders/by_day${qs(params)}`);
  return res.json();
};

export const getFbsOrdersHeatmap = async (
  params: FbsOrdersRangeParams
): Promise<FbsOrdersHeatmap> => {
  const res = await fetchApi(`/api/fbs_orders/heatmap${qs(params)}`);
  return res.json();
};

export type FbsOpenSuppliesByWarehouse = {
  from: string;
  to: string;
  timezone: string;
  warehouses: Array<{
    key: string;
    self_warehouse_id: number | null;
    warehouse_name: string;
  }>;
  day_rows: Array<{
    day: string;
    by_warehouse: Record<string, number>;
    total: number;
  }>;
  totals_by_warehouse: Record<string, number>;
  total_orders: number;
};

export const getFbsOrdersCancellations = async (
  params: FbsOrdersRangeParams
): Promise<FbsOrdersCancellations> => {
  const res = await fetchApi(`/api/fbs_orders/cancellations${qs(params)}`);
  return res.json();
};

export const getFbsOpenSuppliesByWarehouse = async (
  params: FbsOrdersRangeParams
): Promise<FbsOpenSuppliesByWarehouse> => {
  const res = await fetchApi(
    `/api/fbs_orders/open_supplies_by_warehouse${qs(params)}`
  );
  return res.json();
};

export const postFbsOrdersSync = async () => {
  const res = await fetchApi(`/api/fbs_orders/sync`, { method: "POST" });
  return res.json();
};

export const postFbsOrdersBackfill = async (body: {
  from: string;
  to: string;
}) => {
  const res = await fetchApi(`/api/fbs_orders/backfill`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return res.json();
};
